import React, {createContext, useCallback, useContext} from 'react';
import {useHistory} from 'react-router-dom';

import {useResolveLinkTarget} from './resolveLinkTarget';

/** Navigates to a same-origin URL without reloading the page. */
export type NavigateToURL = (url: URL) => void;

/**
 * Lets ARIA's host own navigation to URLs its router may not serve. Under the
 * Forge shell ARIA links across products, and only the shell can route those.
 * Unset, URLs under ARIA's own router are pushed to it; the rest open in a
 * new tab.
 */
export const NavigateToURLContext = createContext<NavigateToURL | undefined>(
  undefined
);

export function useNavigateToURL(): NavigateToURL {
  const provided = useContext(NavigateToURLContext);
  const history = useHistory();
  const resolveLinkTarget = useResolveLinkTarget();
  return useCallback(
    url => {
      if (provided) {
        provided(url);
        return;
      }
      // A router path when the URL is on this origin under the router's
      // mount; otherwise the URL comes back unchanged.
      const target = resolveLinkTarget(url.href);
      if (target === url.href) {
        // Already absolute; the app prefix does not apply outside its router.
        // oxlint-disable-next-line wandb/no-unprefixed-urls
        window.open(url.href, '_blank', 'noopener');
      } else {
        history.push(target);
      }
    },
    [history, provided, resolveLinkTarget]
  );
}

/** Runs `navigate` for a plain left click; other clicks keep anchor behavior. */
export const onPlainClick =
  (navigate: () => void) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    const isPlain =
      event.button === 0 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey;
    if (isPlain) {
      event.preventDefault();
      navigate();
    }
  };
